import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Inline expandable search component for desktop header
 * Expands inline within the header row instead of opening a modal
 */
class InlineSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchContainer', 'searchButton', 'closeButton', 'predictiveSearchResults'];

  #controller = new AbortController();
  #activeFetch = null;
  #isExpanded = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    // Only enable on desktop (≥1024px)
    if (window.innerWidth < 1024) {
      return;
    }

    this.refs.searchButton?.addEventListener('click', this.#handleSearchClick, { signal });
    this.refs.closeButton?.addEventListener('click', this.#handleCloseClick, { signal });
    this.refs.searchInput?.addEventListener('input', debounce(this.#handleSearch, 300), { signal });
    this.refs.searchInput?.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Close on click outside
    document.addEventListener('click', this.#handleClickOutside, { signal });

    // Close on escape key
    document.addEventListener('keydown', this.#handleEscapeKey, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  #handleSearchClick = (event) => {
    event.stopPropagation();
    if (!this.#isExpanded) {
      this.#expandSearch();
    }
  };

  #handleCloseClick = (event) => {
    event.stopPropagation();
    this.#collapseSearch();
  };

  #handleClickOutside = (event) => {
    if (this.#isExpanded && !this.contains(event.target)) {
      this.#collapseSearch();
    }
  };

  #handleEscapeKey = (event) => {
    if (event.key === 'Escape' && this.#isExpanded) {
      this.#collapseSearch();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Enter' && this.refs.searchInput.value.trim()) {
      event.preventDefault();
      this.#submitSearch();
    }
  };

  #expandSearch = () => {
    if (this.#isExpanded || window.innerWidth < 1024) return;

    this.#isExpanded = true;
    this.setAttribute('expanded', '');
    
    // Focus input after animation
    requestAnimationFrame(() => {
      this.refs.searchInput?.focus();
    });
  };

  #collapseSearch = () => {
    if (!this.#isExpanded) return;

    this.#isExpanded = false;
    this.removeAttribute('expanded');
    this.#resetSearch();
    this.refs.searchInput?.blur();
  };

  #handleSearch = async () => {
    const query = this.refs.searchInput?.value.trim() || '';

    if (query.length === 0) {
      this.#resetSearch();
      return;
    }

    if (query.length < 2) {
      this.#clearResults();
      return;
    }

    await this.#fetchSearchResults(query);
  };

  #fetchSearchResults = async (query) => {
    // Cancel previous request
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const url = new URL(`${Theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`);
      
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Search failed');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const resultsContainer = doc.querySelector('[id^="predictive-search-results"]');

      if (resultsContainer && this.refs.predictiveSearchResults) {
        await morph(this.refs.predictiveSearchResults, resultsContainer, {
          duration: prefersReducedMotion() ? 0 : 200,
        });
        this.#updateResultsAccessibility();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    }
  };

  #updateResultsAccessibility = () => {
    if (this.refs.searchInput && this.refs.predictiveSearchResults) {
      const hasResults = this.refs.predictiveSearchResults.children.length > 0;
      this.refs.searchInput.setAttribute('aria-expanded', hasResults ? 'true' : 'false');
      // Show/hide results container
      if (hasResults) {
        this.refs.predictiveSearchResults.style.display = 'block';
      } else {
        this.refs.predictiveSearchResults.style.display = 'none';
      }
    }
  };

  #resetSearch = () => {
    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }
    this.#clearResults();
  };

  #clearResults = () => {
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.innerHTML = '';
    }
    this.#updateResultsAccessibility();
  };


  #submitSearch = () => {
    const query = this.refs.searchInput?.value.trim();
    if (query) {
      window.location.href = `${Theme.routes.search_url}?q=${encodeURIComponent(query)}&type=product`;
    }
  };
}

customElements.define('inline-search', InlineSearchComponent);

